import {
  Injectable,
  ComponentFactoryResolver,
  ApplicationRef,
  Injector,
  EmbeddedViewRef,
  ComponentRef,
  Renderer2,
  RendererFactory2
} from '@angular/core';
import { IMiningToast, IMiningToastCollection } from '../interfaces';
import { MiningToastComponent } from '../components/mining-toast/mining-toast.component';

@Injectable({
  providedIn: 'root'
})
export class MiningToastManagerService {

  private toastCollection: IMiningToastCollection[];

  private toastContainer: any;
  private renderer2: Renderer2;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
    private rendererFactory: RendererFactory2
  ) {
    this.toastCollection = [];
    this.renderer2 = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * Function that create a reference to MiningToast component
   * @param miningToast: Configuration to create a toast
   */
  public createToast(miningToast: IMiningToast): void {
    // If can't find a toast with same message, create a new
    if (!this.getToastByMessage(miningToast.message)) {
      // Create the component reference
      const componentRef = this.componentFactoryResolver
        .resolveComponentFactory(MiningToastComponent)
        .create(this.injector);

      // Set input param
      componentRef.instance.miningToast = miningToast;

      // Create a toast instance
      const newToast = {
        componentRef,
        toastConfig: miningToast,
        timeOut: setTimeout(() => {
          this.destroyToastComponent(componentRef, miningToast);
        }, miningToast.config.delay)
      };

      this.toastCollection.push(newToast);

      this.toastCollection[this.toastCollection.length - 1]
        .componentRef.instance.closeToast.subscribe((isClosed: boolean) => {
          if (isClosed) {
            clearTimeout(newToast.timeOut);
            this.destroyToastComponent(componentRef, miningToast);
          }
        });

      // Add the component reference to application reference
      this.appRef.attachView(componentRef.hostView);

      // Get DOM component
      const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;

      // Create a container for toast in application body
      this.createToastContainer();

      // Add the component DOM to application DOM
      this.toastContainer.appendChild(domElem);
    }
  }

  /**
   * Function that delete the toast instance
   * @param componentRef: Component reference
   * @param miningToastConfig: Configuration to get the function and execute it
   */
  private destroyToastComponent(componentRef: ComponentRef<MiningToastComponent>, miningToast: IMiningToast): void {
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();

    this.toastCollection.splice(this.toastCollection.indexOf(this.getToastByMessage(miningToast.message)));
    if (miningToast.config.onClose) {
      miningToast.config.onClose();
    }
  }

  /**
   * Function that create a container for toast, and can stack it
   */
  private createToastContainer(): void {
    const container = this.renderer2.createElement('div');
    if (!this.toastCollection.length && this.toastContainer) {
      this.renderer2.removeChild(document.body, container);
      this.toastContainer = null;

    } else if (this.toastCollection.length && !this.toastContainer) {
      this.renderer2.addClass(container, 'flex-col');
      this.renderer2.addClass(container, 'toast-container');
      this.renderer2.appendChild(document.body.children[0], container);
      this.toastContainer = this.renderer2.selectRootElement('.toast-container');
    }
  }

  /**
   * Function that obtains the toast by message to prevent duplicates
   * @param message: Message to get toast
   */
  private getToastByMessage(message: string): any {
    let toastExists = null;
    if (this.toastCollection.length) {
      const foundToast = this.toastCollection.find((toast) => toast.toastConfig.message === message);
      toastExists = foundToast ? foundToast : null;
    }
    return toastExists;
  }
}
